/**
 * AGC: Potion Mint Success Modal
 * AGC: Displays success message with potion rarity and details after minting
 * @author AGC (AI-Generated Code) with Cursor
 */

import React from 'react'
import { X, ExternalLink } from 'lucide-react'
import { CLASSIC_POTION_TYPES } from '../ClassicPotionCard'

// AGC: Rarity names and colors
const RARITY_INFO = {
  0: { name: 'Common', color: 'text-white', bgColor: 'bg-gray-500', borderColor: 'border-white' },
  1: { name: 'Uncommon', color: 'text-green-400', bgColor: 'bg-green-500', borderColor: 'border-green-500' },
  2: { name: 'Rare', color: 'text-blue-400', bgColor: 'bg-blue-500', borderColor: 'border-blue-500' },
  3: { name: 'Epic', color: 'text-purple-400', bgColor: 'bg-purple-500', borderColor: 'border-purple-500' },
  4: { name: 'Legendary', color: 'text-orange-400', bgColor: 'bg-orange-500', borderColor: 'border-orange-500' }
} as const

interface PotionMintSuccessModalProps {
  isOpen: boolean
  onClose: () => void
  potionData: {
    tokenId: number
    potionType: number
    rarity: number
    magnitude?: number
    txHash?: string
  } | null
}

/**
 * AGC: Potion Mint Success Modal - Shows rarity and potion details
 */
export const PotionMintSuccessModal: React.FC<PotionMintSuccessModalProps> = ({
  isOpen,
  onClose,
  potionData
}) => {
  if (!isOpen || !potionData) return null

  const potionType = CLASSIC_POTION_TYPES.find(p => p.id === potionData.potionType)
  const rarityInfo = RARITY_INFO[potionData.rarity as keyof typeof RARITY_INFO] || RARITY_INFO[0]
  const baseExplorerUrl = import.meta.env.VITE_BASE_EXPLORER_URL || 'https://sepolia.basescan.org'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl border-2 border-purple-500/50 p-6 max-w-md w-full mx-4 shadow-2xl">
        {/* AGC: Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* AGC: Success header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-white mb-2">Potion Minted Successfully!</h2>
          <p className="text-slate-400 text-sm">Your new potion is ready for battle</p>
        </div>

        {/* AGC: Potion details */}
        <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-center mb-4">
            <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${potionType?.color || 'from-gray-600 to-gray-700'} flex items-center justify-center text-4xl border-4 ${rarityInfo.borderColor}`}>
              {potionType?.emoji || '🧪'}
            </div>
          </div>

          <div className="text-center space-y-2">
            <h3 className="text-xl font-bold text-white">{potionType?.name || 'Unknown Potion'}</h3>
            <div className={`inline-block px-3 py-1 rounded-full ${rarityInfo.bgColor} ${rarityInfo.color} font-bold text-sm`}>
              {rarityInfo.name}
            </div>
            {potionData.magnitude !== undefined && (
              <p className="text-slate-300 text-sm">
                Magnitude: {potionData.magnitude}
              </p>
            )}
            <p className="text-slate-400 text-xs">
              Token ID: #{potionData.tokenId}
            </p>
          </div>
        </div>

        {/* AGC: Transaction link */}
        {potionData.txHash && (
          <div className="mb-4 space-y-2">
            <a
              href={`${baseExplorerUrl}/tx/${potionData.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              View Transaction on BaseScan
            </a>
            {/* AGC: Verify Rarity Link */}
            <a
              href={`${baseExplorerUrl}/address/${import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '0xcc5B257fA0109Bf20D37b0147A352E617fCca762'}#events`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-purple-400 hover:text-purple-300 text-xs transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              🔍 Verify Rarity on BaseScan (Token #{potionData.tokenId})
            </a>
          </div>
        )}

        {/* AGC: Close button */}
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold py-3 px-4 rounded-lg transition-all"
        >
          Close
        </button>
      </div>
    </div>
  )
}

